<template>
    <DialogLayout :class-name="'edit-message-dialog'" @close="handleClose">
        <template #title>{{ t('candidate.editMessage') }}</template>
        <template #default>
            <el-form ref="formRef" :model="formData" :rules="rules"
                class="edit-message-form">
                <el-form-item prop="content">
                    <el-input v-model="formData.content" type="textarea" :placeholder="t('common.pleaseEnter')" :rows="4"/>
                </el-form-item>
            </el-form>
        </template>
        <template #footer>
            <el-button class="confirm-button-default" @click="handleCancel">
                {{ t('common.cancel') }}
            </el-button>
            <el-button type="primary" :loading="submitting" @click="handleConfirm">
                {{ t('common.save') }}
            </el-button>
        </template>
    </DialogLayout>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import DialogLayout from './dialog-layout.vue'
import type { EditMessageDialogProps } from './edit-message-dialog-types'
import { updateCandidateMessageApi, updateRecruiterMessageApi } from '@/api/candidate'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const props = defineProps<EditMessageDialogProps>()

const emit = defineEmits<{
    (e: 'confirm'): void
    (e: 'cancel'): void
    (e: 'close'): void
}>()

const formRef = ref<FormInstance>()
const submitting = ref(false)

// 表单数据
const formData = reactive<{
    content: string
}>({
    content: ''
})

// 表单验证规则
const rules: FormRules = {
    content: [
        { required: true, message: t('candidate.pleaseEnterContent'), trigger: 'blur' }
    ]
}

// 初始化表单数据（回显）
onMounted(() => {
    if (props.content) {
        formData.content = props.content
    }
})

// 确认保存
const handleConfirm = async () => {
    if (!formRef.value) return

    try {
        // 先验证表单，如果验证失败会抛出错误，不会继续执行
        await formRef.value.validate()
    } catch (error: unknown) {
        // 表单验证失败，不显示额外提示（Element Plus 会自动显示），直接返回，不提交接口
        if (error && typeof error === 'object' && 'fields' in error) {
            return
        }
        // 其他验证错误也直接返回
        return
    }

    // 验证通过后，才执行 API 调用
    try {
        submitting.value = true

        const payload = {
            content: formData.content.trim() || ''
        }
        // 调用 API 更新留言
        if (props.messageType === 'recruiter') {
            await updateRecruiterMessageApi({
                ...payload,
                contactId: props.messageId
            })
        } else {
            await updateCandidateMessageApi({
                messageId: props.messageId,
                ...payload
            })
        }

        ElMessage.success(t('common.saveSuccess'))
        submitting.value = false
        emit('confirm')
    } catch (error: unknown) {
        submitting.value = false
        
        // API 调用失败
        const errorMessage = error && typeof error === 'object' && 'message' in error 
            ? String(error.message) 
            : t('common.saveFailed')
        console.error('保存失败:', error)
        ElMessage.error(errorMessage)
    }
}

// 取消
const handleCancel = () => {
    emit('cancel')
}

// 关闭
const handleClose = () => {
    emit('close')
}
</script>

<style scoped lang="scss">
.edit-message-form {

    :deep(.el-form-item__label) {
        font-size: 14px;
        font-weight: 500;
        color: #212429;
        line-height: 35px;
        text-align: right;
        padding-right: 12px;
        margin-bottom: 0px;

        &::before {
            display: none;
        }
    }

    :deep(.el-textarea) {
        .el-textarea__inner {
            font-size: 14px;
            color: #495057;
            line-height: 22px;
            resize: none;

            &::placeholder {
                color: #C9D3D9;
            }
        }
    }

    :deep(.el-input__count) {
        font-size: 12px;
        color: #86909C;
        background: transparent;
    }
}

:deep(.edit-message-dialog) {
    width: 512px;

    
}
</style>

<style lang="scss">
.edit-message-dialog {
    width: 512px !important;
    .dialog-footer {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
            padding: 20px 30px;
    
            .el-button {
                min-width: 80px;
            }
        }
}
</style>

